import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Shield, Loader2, KeyRound, Download, Eye, EyeOff, AlertTriangle, Upload as UploadIcon, CheckCircle2, GitBranch } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from '../../hooks/use-toast';

import { API } from '../../config';

const KeystoreSection = ({ token }) => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [showCreds, setShowCreds] = useState(false);
  const [creds, setCreds] = useState(null);
  const [credsLoading, setCredsLoading] = useState(false);
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/mobile/keystore`, { headers: { Authorization: `Bearer ${token()}` } });
      setInfo(r.data);
    } catch (e) {
      toast({ title: 'Failed to load keystore info' });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const generate = async (force = false) => {
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/admin/mobile/keystore/generate`,
        { force, alias: 'trainpanda-release' },
        { headers: { Authorization: `Bearer ${token()}` }, timeout: 60000 });
      setInfo(r.data);
      setCreds(null); setShowCreds(false);
      toast({ title: force ? 'Keystore replaced' : 'Keystore generated', description: r.data.sha1 });
      setConfirmOverwrite(false);
    } catch (e) {
      toast({ title: 'Generation failed', description: (e.response?.data?.detail || e.message)?.slice(0, 220) });
    } finally { setGenerating(false); }
  };

  const fetchCreds = async () => {
    setCredsLoading(true);
    try {
      const r = await axios.get(`${API}/admin/mobile/keystore/credentials`, { headers: { Authorization: `Bearer ${token()}` } });
      setCreds(r.data);
      setShowCreds(true);
    } catch (e) {
      toast({ title: 'Failed to fetch credentials' });
    } finally { setCredsLoading(false); }
  };

  const pushToGitHub = async () => {
    setPushing(true);
    try {
      const r = await axios.post(`${API}/admin/mobile/keystore/push-to-github`, {}, { headers: { Authorization: `Bearer ${token()}` } });
      if (r.data.ok) {
        toast({ title: 'Pushed to GitHub Secrets', description: 'AAB builds will now sign with your release key.' });
      } else {
        toast({ title: 'Partial push', description: JSON.stringify(r.data.secrets) });
      }
      load();
    } catch (e) {
      toast({ title: 'Push failed', description: (e.response?.data?.detail || e.message)?.slice(0, 220) });
    } finally { setPushing(false); }
  };

  const downloadJks = () => {
    const url = `${API}/admin/mobile/keystore/download?token=${encodeURIComponent(token())}`;
    // anchor with auth header isn't possible from browser; use a server endpoint that accepts ?token=
    // Backend uses get_admin which reads from header; we need to fetch + blob download.
    fetch(url, { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => { if (!r.ok) throw new Error('Download failed'); return r.blob(); })
      .then(blob => {
        const u = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = u;
        a.download = `trainpanda-release.jks`;
        document.body.appendChild(a); a.click();
        a.remove(); window.URL.revokeObjectURL(u);
      })
      .catch(e => toast({ title: 'Download failed', description: e.message }));
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 card-shadow p-6 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-bold text-[#0B2E5C] flex items-center gap-2"><KeyRound size={16} className="text-tp-orange" /> 4. Play-Store Keystore (AAB Signing)</h3>
          <p className="text-xs text-gray-500 mt-1">A Java keystore (.jks) signs your AAB so it can be uploaded to Play Store. <b>Once published, the same keystore must sign every future update</b> — back it up safely.</p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-6 text-gray-400"><Loader2 className="animate-spin mx-auto" size={20} /></div>
      ) : !info?.exists ? (
        <div className="border border-dashed rounded-2xl p-6 text-center">
          <Shield size={32} className="mx-auto text-gray-300 mb-2" />
          <div className="text-sm font-semibold text-gray-700 mb-1">No keystore yet</div>
          <div className="text-xs text-gray-500 mb-4">Generate a Play-Store-grade key (RSA 2048, ~27-year validity).</div>
          <Button onClick={() => generate(false)} disabled={generating} className="btn-violet rounded-full" data-testid="generate-keystore-btn">
            {generating ? <Loader2 className="animate-spin mr-1.5" size={14} /> : <KeyRound size={14} className="mr-1.5" />} Generate Keystore
          </Button>
        </div>
      ) : (
        <>
          <div className="grid md:grid-cols-2 gap-3 text-xs">
            <Stat label="Alias" value={info.alias} mono />
            <Stat label="Created" value={info.created_at ? new Date(info.created_at).toLocaleString() : '—'} />
            <Stat label="SHA-1 Fingerprint" value={info.sha1 || '—'} mono small />
            <Stat label="SHA-256 Fingerprint" value={info.sha256 || '—'} mono small />
            <Stat label="Validity" value={`${info.validity_days || '—'} days`} />
            <Stat label="Key size" value={info.key_size ? `RSA ${info.key_size}` : '—'} />
            <Stat label="Distinguished Name" value={info.distinguished_name || '—'} mono small />
            <Stat label="Pushed to GitHub" value={info.pushed_to_github_at ? `${new Date(info.pushed_to_github_at).toLocaleString()} (${info.last_push_status || ''})` : 'Not yet pushed'} />
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <Button onClick={downloadJks} variant="outline" data-testid="download-keystore-btn"><Download size={14} className="mr-1.5" /> Download .jks</Button>
            <Button onClick={fetchCreds} variant="outline" disabled={credsLoading}>
              {credsLoading ? <Loader2 className="animate-spin mr-1.5" size={14} /> : (showCreds ? <EyeOff size={14} className="mr-1.5" /> : <Eye size={14} className="mr-1.5" />)} Show passwords
            </Button>
            <Button onClick={pushToGitHub} disabled={pushing} className="btn-violet rounded-full" data-testid="push-keystore-github">
              {pushing ? <Loader2 className="animate-spin mr-1.5" size={14} /> : <GitBranch size={14} className="mr-1.5" />} Push to GitHub Secrets
            </Button>
            <Button onClick={() => setConfirmOverwrite(true)} variant="outline" className="ml-auto text-red-600 border-red-200 hover:bg-red-50">
              <AlertTriangle size={14} className="mr-1.5" /> Regenerate (replaces key)
            </Button>
          </div>

          {showCreds && creds && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-2">
              <div className="text-xs font-bold text-amber-900 flex items-center gap-1"><AlertTriangle size={14} /> Keep these somewhere safe — needed to sign future updates</div>
              <CredRow label="Store Password" value={creds.store_password} />
              <CredRow label="Key Password" value={creds.key_password} />
              <CredRow label="Alias" value={creds.alias} />
            </div>
          )}
        </>
      )}

      {/* Overwrite confirmation */}
      <Dialog open={confirmOverwrite} onOpenChange={setConfirmOverwrite}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><AlertTriangle size={18} /> Replace keystore?</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-gray-700 space-y-2">
            <p><b>Warning:</b> Replacing your keystore means you will <u>not be able to publish further updates</u> to the Play Store version signed with the previous key.</p>
            <p>Only do this if:</p>
            <ul className="list-disc pl-5 space-y-1 text-xs">
              <li>You haven't published to the Play Store yet, OR</li>
              <li>You've enabled Play App Signing (Google holds the master key)</li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOverwrite(false)}>Cancel</Button>
            <Button onClick={() => generate(true)} disabled={generating} className="bg-red-600 hover:bg-red-700 text-white">
              {generating ? <Loader2 className="animate-spin mr-1.5" size={14} /> : <KeyRound size={14} className="mr-1.5" />} Yes, replace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Stat = ({ label, value, mono, small }) => (
  <div className="bg-gray-50 rounded-lg p-2.5">
    <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{label}</div>
    <div className={`mt-0.5 ${mono ? 'font-mono' : ''} ${small ? 'text-[11px]' : 'text-xs'} text-gray-800 break-all`}>{value || '—'}</div>
  </div>
);

const CredRow = ({ label, value }) => {
  const [shown, setShown] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(value || ''); toast({ title: 'Copied' }); };
  return (
    <div className="flex items-center gap-2">
      <div className="text-xs font-semibold text-amber-900 w-32 shrink-0">{label}</div>
      <Input type={shown ? 'text' : 'password'} value={value || ''} readOnly className="font-mono text-xs h-8" />
      <Button size="sm" variant="outline" onClick={() => setShown(s => !s)} className="h-8 px-2">{shown ? <EyeOff size={12} /> : <Eye size={12} />}</Button>
      <Button size="sm" variant="outline" onClick={copy} className="h-8 px-2">Copy</Button>
    </div>
  );
};

export default KeystoreSection;
